﻿import { useState } from "react";
import TopBar from "@/pages/home/components/TopBar";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";
import StickyMobile from "@/pages/home/components/StickyMobile";

function SchemaMarkup() {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Ramen Reparatie Eindhoven",
    provider: {
      "@type": "LocalBusiness",
      name: "Ramen Repair Eindhoven",
      url: "https://ramen-repair-eindhoven.nl/",
      telephone: "+31407987470",
      areaServed: {
        "@type": "City",
        name: "Eindhoven",
      },
    },
    serviceType: "Ramenreparatie",
    description:
      "Professionele ramen reparatie in Eindhoven. Van klemmende ramen tot gebroken glas. 48u-spoedservice, vaste prijzen vanaf €75, 10 jaar garantie.",
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "EUR",
      lowPrice: "45",
      highPrice: "350",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://ramen-repair-eindhoven.nl/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Diensten",
        item: "https://ramen-repair-eindhoven.nl/onze-diensten/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Ramen reparatie",
        item: "https://ramen-repair-eindhoven.nl/ramen-reparatie/",
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
    </>
  );
}

function Breadcrumb() {
  return (
    <nav aria-label="Kruimelpad" className="bg-white border-b border-brand-border">
      <div className="max-w-[1440px] mx-auto px-4 md:px-10 py-3">
        <ol className="flex items-center gap-2 text-sm font-geist text-brand-text/50 flex-wrap">
          <li>
            <a href="/" className="hover:text-brand-blue transition-colors">Home</a>
          </li>
          <li className="flex items-center gap-2">
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center text-xs"></i>
            <a href="/onze-diensten/" className="hover:text-brand-blue transition-colors">Diensten</a>
          </li>
          <li className="flex items-center gap-2">
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center text-xs"></i>
            <span className="text-brand-text">Ramen reparatie</span>
          </li>
        </ol>
      </div>
    </nav>
  );
}

function SectionHero() {
  const usps = ["Vanaf €75", "Klaar in 30-60 min", "Alle merken & types"];

  return (
    <section className="bg-brand-text">
      <div className="max-w-[1440px] mx-auto px-4 md:px-10 py-16 md:py-24 lg:py-32">
        <div className="max-w-3xl">
          <span className="inline-block self-start font-geist-mono text-xs md:text-sm font-bold tracking-wider text-brand-text bg-brand-yellow px-3 py-1.5 rounded-full mb-6">
            DIENST — RAMENREPARATIE
          </span>
          <h1 className="font-geist font-bold text-3xl md:text-4xl lg:text-5xl text-white tracking-tight mb-5">
            Raam- en ramenreparatie in Eindhoven
          </h1>
          <p className="font-geist text-base md:text-lg text-white/70 leading-relaxed mb-8 max-w-2xl">
            Snel en vakkundig ramen repareren of vervangen in heel Eindhoven. Van klemmende ramen tot gebroken glas. 48u-spoedservice, 10 jaar garantie.
          </p>
          <ul className="flex flex-col gap-2 mb-8">
            {usps.map((usp, i) => (
              <li key={i} className="flex items-center gap-2.5">
                <div className="w-5 h-5 flex items-center justify-center rounded-full bg-brand-yellow">
                  <i className="ri-check-line text-brand-text text-xs w-4 h-4 flex items-center justify-center"></i>
                </div>
                <span className="font-geist text-sm text-white/90">{usp}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <a
              href="tel:+31407987470"
              className="inline-flex items-center justify-center gap-2 bg-brand-yellow text-brand-text font-geist font-semibold text-sm px-6 py-3.5 rounded-md hover:bg-brand-yellow/90 transition-colors whitespace-nowrap"
            >
              <i className="ri-phone-line w-5 h-5 flex items-center justify-center text-base"></i>
              Bel 040 798 7470
            </a>
            <a
              href="/contact/"
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white font-geist font-medium text-sm px-6 py-3.5 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap"
            >
              <i className="ri-file-list-line w-5 h-5 flex items-center justify-center text-base"></i>
              Gratis offerte
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionWanneer() {
  const symptoms = [
    {
      title: "Raam klemt of sluit niet meer",
      desc: "Door jarenlang gebruik, temperatuurwisselingen en uitloop van het materiaal sluit een raam vaak niet meer goed. Dit levert tocht, geluid en hogere energiekosten op. Wij stellen het raam professioneel af, vervangen versleten scharnieren en monteren nieuw beslag zodat het weer vlot opent en sluit.",
    },
    {
      title: "Gebroken glas of beschadigde ruit",
      desc: "Een gebroken raam is een veiligheidsrisico en leidt tot warmteverlies. Wij meten in, leveren op maat gezette beglazing en plaatsen deze vakkundig. Van enkel glas tot HR++ isolatieglas — wij adviseren welke glassoort het beste past bij uw woning en budget.",
    },
    {
      title: "Beslag en sluitwerk versleten",
      desc: "Het beslag van een raam — meerpuntssluitingen, scharnieren en sluitstukken — slijt na 10 tot 20 jaar. Versleten beslag zorgt voor een slechte sluiting en kan het raam beschadigen. Wij vervangen het beslag door originele onderdelen van merken zoals Roto, Winkhaus, Maco en Sobinco.",
    },
    {
      title: "Condens tussen dubbel glas",
      desc: "Als u condens ziet tussen de glasplaten van een dubbel raam, is de isolerende laag lek. Dit betekent dat het glas zijn isolerende werking is kwijtgeraakt. Wij vervangen het glas inclusief de warmte-edge en plaatsen nieuw HR++ glas. Dit bespaart direct op uw stookkosten.",
    },
    {
      title: "Raamkruk kapot of versleten",
      desc: "Een kapotte of versleten raamkruk is het meest voorkomende raamprobleem. De kruk draait door, breekt af of sluit het raam niet meer af. Wij vervangen raamkrukken in alle kleuren en merken — wit, RAL-kleuren, RVS en brons — vaak binnen 15 minuten per kruk.",
    },
    {
      title: "Scharnieren roestig of los",
      desc: "Scharnieren dragen het volle gewicht van het raam. Na jaren kunnen ze roesten, lossen of uit hun voering raken. Dit zorgt voor een scheef hangend raam dat niet meer sluit. Wij vervangen of bijstellen scharnieren en monteren RVS-scharnieren die weer jaren meegaan.",
    },
  ];

  return (
    <section className="max-w-[960px] mx-auto px-4 md:px-6 py-12 md:py-16">
      <h2 className="font-geist font-bold text-2xl md:text-3xl text-brand-text tracking-tight mb-5">
        Wanneer is ramen reparatie nodig ?
      </h2>
      <p className="font-geist text-base md:text-lg text-brand-text/70 leading-relaxed mb-8">
        Een raam repareren is meestal slimmer dan een raam vervangen. De meeste problemen zijn gericht op te lossen zonder het hele kozijn te vernieuwen. Hieronder vindt u de meest voorkomende situaties waarbij wij u direct kunnen helpen met professionele ramen reparatie in Eindhoven :
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
        {symptoms.map((s, i) => (
          <div key={i} className="bg-white border border-brand-border/40 rounded-xl p-5 md:p-6">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-yellow/15 shrink-0">
                <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center text-brand-yellow text-sm"></i>
              </span>
              <h3 className="font-geist font-semibold text-sm md:text-base text-brand-text">{s.title}</h3>
            </div>
            <p className="font-geist text-sm text-brand-text/70 leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionDiensten() {
  const diensten = [
    { title: "Kozijnreparatie", desc: "Kozijn reparatie voor PVC, hout en aluminium. Scharnieren, beslag, sluitwerk en afdichtingen. Vanaf €75.", href: "/kozijn-reparatie/" },
    { title: "Draaikiepraam reparatie", desc: "Precisie-afstelling en reparatie van draaikiepramen. Roto, Winkhaus, Maco, Sobinco. Vanaf €75.", href: "/draaikiepraam-reparatie/" },
    { title: "Glas vervangen inclusief HR++", desc: "Gebroken ruit of upgrade naar isolatieglas. Wij meten, leveren en plaatsen op maat. Advies ISDE-subsidie.", href: "/glas-vervangen/" },
    { title: "Raamkruk vervangen", desc: "Nieuwe raamkrukken in alle kleuren en merken. Direct gemonteerd, vaak binnen 15 minuten.", href: "/raamkruk-vervangen/" },
    { title: "Schuifpui reparatie", desc: "Rollers, rails en sluitwerk herstellen voor een soepele schuifpui. Alle merken en types.", href: "/schuifpui-reparatie/" },
    { title: "Velux dakraam reparatie", desc: "Lekkende of vastgelopen Velux, Fakro of Roto dakraam. Ook op hoogte uitgerust.", href: "/velux-reparatie/" },
  ];

  return (
    <section className="bg-brand-cream py-12 md:py-16 w-full">
      <div className="max-w-[960px] mx-auto px-4 md:px-6">
        <h2 className="font-geist font-bold text-2xl md:text-3xl text-brand-text tracking-tight mb-5">
          Onze ramen reparatie diensten in Eindhoven
        </h2>
        <p className="font-geist text-base md:text-lg text-brand-text/70 leading-relaxed mb-8">
          Wij zijn specialist in alle vormen van raam reparatie. Of het nu gaat om een klemmend raam, een kapotte raamkruk of gebroken glas — onze vakmannen lossen het op. Hieronder vindt u een overzicht van onze specifieke raam reparatie diensten in Eindhoven, elk met zijn eigen pagina voor meer informatie en tarieven :
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 mb-8">
          {diensten.map((d, i) => (
            <a
              key={i}
              href={d.href}
              className="group bg-white border border-brand-border/40 rounded-xl p-5 md:p-6 hover:border-brand-yellow/50 hover:shadow-md transition-all duration-200"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-geist font-semibold text-sm md:text-base text-brand-text group-hover:text-brand-blue transition-colors">
                  {d.title}
                </h3>
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center text-brand-text/40 group-hover:text-brand-yellow transition-colors"></i>
              </div>
              <p className="font-geist text-sm text-brand-text/70 leading-relaxed">{d.desc}</p>
            </a>
          ))}
        </div>
        <p className="font-geist text-sm md:text-base text-brand-text/60 leading-relaxed">
          Alle diensten worden uitgevoerd door gecertificeerde monteurs met jarenlange ervaring in Eindhoven en omgeving. Wij werken uitsluitend met originele onderdelen en geven 10 jaar schriftelijke garantie op alle reparaties.
        </p>
      </div>
    </section>
  );
}

function SectionVervangenRepareren() {
  return (
    <section className="max-w-[960px] mx-auto px-4 md:px-6 py-12 md:py-16">
      <h2 className="font-geist font-bold text-2xl md:text-3xl text-brand-text tracking-tight mb-5">
        Raam vervangen of repareren — wat is slimmer ?
      </h2>
      <div className="font-geist text-base md:text-lg text-brand-text/70 leading-relaxed space-y-4">
        <p>
          De meeste raamproblemen zijn te repareren zonder het hele raam te vervangen. Een raam repareren kost doorgaans 5 tot 10 keer minder dan een raam vervangen en levert hetzelfde resultaat: een raam dat weer soepel opent, goed sluit en goed isoleert. Wij geven altijd eerlijk advies: herstellen of vervangen ?
        </p>
        <p>
          <strong className="text-brand-text">Repareren is de slimste keuze bij :</strong> klemmende ramen, versleten beslag, losse scharnieren, kapotte raamkrukken, lekkende afdichtingen en lichte beschadigingen aan het kozijn. Onze monteurs stellen af, vervangen onderdelen en monteren nieuwe afdichtingen. Vaak bent u binnen 30 tot 60 minuten klaar.
        </p>
        <p>
          <strong className="text-brand-text">Vervangen is verstandig bij :</strong> ernstig beschadigde ruiten, verouderd enkel glas dat u wilt upgraden naar HR++ isolatieglas, of een kozijn dat structureel beschadigd is. Bij een raam vervangen in Eindhoven adviseren wij over de beste materialen en beglazing. Wij meten gratis in en geven een transparante prijsopgave.
        </p>
        <p>
          <strong className="text-brand-text">ISDE-subsidie 2026 :</strong> vervangt u enkel glas door energiezuinig HR++ beglazing, dan komt u mogelijk in aanmerking voor de ISDE-subsidie. In 2026 kan dit oplopen tot duizenden euro&apos;s. Wij adviseren u gratis over de mogelijkheden en helpen bij de aanvraag. Lees meer over de <a href="/blog/isde-subsidie-2026/" className="text-brand-blue hover:underline">ISDE-subsidie 2026</a>.
        </p>
      </div>
    </section>
  );
}

function SectionPrijzen() {
  const rows = [
    { dienst: "Raam afstellen", prijs: "Vanaf €75", tijd: "30-60 min" },
    { dienst: "Beslag / scharnier vervangen", prijs: "€85 – €150", tijd: "45 min – 1u" },
    { dienst: "Raamkruk vervangen", prijs: "€45 – €95", tijd: "15-30 min" },
    { dienst: "Glas vervangen per ruit", prijs: "€120 – €350", tijd: "1-2u" },
    { dienst: "HR++ glas per m²", prijs: "Vanaf €175", tijd: "1-2u" },
    { dienst: "Voorrijkosten binnen 15 km", prijs: "Gratis", tijd: "-" },
  ];

  return (
    <section className="bg-brand-cream py-12 md:py-16 w-full">
      <div className="max-w-[960px] mx-auto px-4 md:px-6">
        <h2 className="font-geist font-bold text-2xl md:text-3xl text-brand-text tracking-tight mb-5">
          Wat kost ramen reparatie in Eindhoven ?
        </h2>
        <p className="font-geist text-base md:text-lg text-brand-text/70 leading-relaxed mb-6">
          De kosten voor ramen reparatie hangen af van het type probleem en de benodigde materialen. Wij hanteren vaste tarieven en geven altijd een prijsopgave vooraf, zodat u nooit voor verrassingen komt te staan. Hieronder vindt u een overzicht van de meest voorkomende raam reparatie werkzaamheden en bijbehorende tarieven :
        </p>
        <div className="overflow-x-auto mb-6">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-brand-text text-white">
                <th className="px-4 md:px-5 py-3 font-geist font-semibold text-sm whitespace-nowrap">Dienst</th>
                <th className="px-4 md:px-5 py-3 font-geist font-semibold text-sm whitespace-nowrap">Prijs</th>
                <th className="px-4 md:px-5 py-3 font-geist font-semibold text-sm whitespace-nowrap">Tijdsduur</th>
              </tr>
            </thead>
            <tbody className="font-geist text-sm text-brand-text/80">
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-brand-border/30 bg-white">
                  <td className="px-4 md:px-5 py-3.5">{r.dienst}</td>
                  <td className="px-4 md:px-5 py-3.5 font-semibold">{r.prijs}</td>
                  <td className="px-4 md:px-5 py-3.5">{r.tijd}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="font-geist text-sm text-brand-text/60 leading-relaxed">
          Alle prijzen zijn exclusief 21% BTW en inclusief arbeid. Materialen worden doorberekend tegen werkelijke kostprijs. Wij geven altijd een vaste prijsopgave vooraf, zodat u precies weet waar u aan toe bent. Voorrijkosten zijn gratis binnen 15 kilometer rond Eindhoven.
        </p>
      </div>
    </section>
  );
}

function SectionWaaromWij() {
  const points = [
    { text: "Lokaal aanwezig : monteurs werken dagelijks in Eindhoven en omgeving" },
    { text: "48u-spoedservice : binnen 48 uur bij u op locatie, ook voor spoedklussen" },
    { text: "Vaste tarieven : transparante prijsopgave vooraf, geen verrassingen achteraf" },
    { text: "10 jaar garantie schriftelijk op alle reparaties en vervangingen" },
  ];

  return (
    <section className="max-w-[960px] mx-auto px-4 md:px-6 py-12 md:py-16">
      <h2 className="font-geist font-bold text-2xl md:text-3xl text-brand-text tracking-tight mb-5">
        Waarom Ramen Repair Eindhoven ?
      </h2>
      <p className="font-geist text-base md:text-lg text-brand-text/70 leading-relaxed mb-6">
        Als lokale vakman uit Brabant kennen wij Eindhoven door en door. Wij weten welke raamtypen en kozijnsoorten hier voorkomen, van de jaren &apos;50-rijtjeshuizen in Stratum tot de moderne appartementen in Strijp-S. Daardoor werken wij sneller, efficiënter en met het juiste gereedschap en materiaal voor elke situatie.
      </p>
      <ul className="space-y-3">
        {points.map((p, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-brand-yellow/15 shrink-0 mt-0.5">
              <i className="ri-check-line w-4 h-4 flex items-center justify-center text-brand-yellow text-sm"></i>
            </span>
            <span className="font-geist text-sm md:text-base text-brand-text/80 leading-relaxed">{p.text}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function SectionFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      q: "Hoe snel kunnen jullie een raam repareren ?",
      a: "De meeste raam reparaties zijn binnen 30 tot 60 minuten uitgevoerd. Eenvoudige klussen zoals een raamkruk vervangen duren 15 minuten. Voor spoedgevallen bieden wij een 48u-service aan, waarbij wij binnen 48 uur bij u op locatie zijn. Bij acute situaties, zoals een gebroken ruit of inbraakschade, streven wij ernaar nog dezelfde dag bij u te zijn.",
    },
    {
      q: "Wat kost een raam reparatie in Eindhoven ?",
      a: "De kosten voor raam reparatie in Eindhoven starten vanaf €75 voor eenvoudig raam afstellen. Een raamkruk vervangen kost €45 tot €95, beslag of scharnieren vervangen €85 tot €150 en glas vervangen €120 tot €350 per ruit. Wij geven altijd een vaste prijsopgave vooraf, inclusief arbeid en materialen tegen werkelijke kostprijs. Voorrijkosten zijn gratis binnen 15 km.",
    },
    {
      q: "Repareren jullie alle merken ramen ?",
      a: "Ja, wij repareren alle gangbare merken ramen en kozijnen. Of het nu gaat om Roto, Winkhaus, Maco, Sobinco, GU of Siegenia beslag — onze monteurs herkennen het merk direct en hebben de juiste originele onderdelen op voorraad. Ook voor oudere series uit de jaren '80 en '90 kunnen wij vaak nog geschikte onderdelen leveren.",
    },
    {
      q: "Kan een klemmend raam gerepareerd of moet vervangen ?",
      a: "In de meeste gevallen is een klemmend raam te repareren. De oorzaak is vaak uitloop van het materiaal, versleten scharnieren of een sluitmechanisme dat is uit afstelling geraakt. Wij stellen het raam professioneel af en vervangen versleten onderdelen. Alleen als het kozijn zelf scheef staat door bouwfouten of ernstig beschadigd is, adviseren wij vervanging. Wij beoordelen dit ter plaatse.",
    },
    {
      q: "Werken jullie ook in wijken rond Eindhoven ?",
      a: "Zeker. Wij zijn actief in alle wijken van Eindhoven: Strijp, Woensel, Centrum, Tongelre, Gestel en Stratum. Ook in omliggende gemeenten zoals Veldhoven, Geldrop, Nuenen, Best, Waalre, Helmond en Son en Breugel. Voorrijkosten zijn gratis binnen 15 kilometer rond het centrum van Eindhoven.",
    },
    {
      q: "Krijg ik garantie op de reparatie ?",
      a: "Ja, wij geven 10 jaar schriftelijke garantie op alle raam reparaties. Deze dekt zowel de arbeid als de gebruikte onderdelen. Bij defecten binnen 10 jaar komen wij kosteloos terug om het probleem te verhelpen. Onze garantiebepalingen zijn helder en zonder kleine lettertjes.",
    },
  ];

  const left = faqs.slice(0, 3);
  const right = faqs.slice(3);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };

  return (
    <section className="bg-white py-12 md:py-16 w-full">
      <div className="max-w-[960px] mx-auto px-4 md:px-6">
        <h2 className="font-geist font-bold text-2xl md:text-3xl text-brand-text tracking-tight mb-8">
          Veelgestelde vragen over ramen reparatie
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          <div className="flex flex-col gap-3">
            {left.map((item, i) => (
              <div key={i} className="border border-brand-border rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left"
                >
                  <span className="font-geist font-semibold text-sm md:text-base text-brand-text">{item.q}</span>
                  <i className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-brand-text/60 flex-shrink-0 transition-transform ${openIndex === i ? "rotate-180" : ""}`}></i>
                </button>
                {openIndex === i && (
                  <div className="px-5 pb-4">
                    <p className="font-geist text-sm text-brand-text/70 leading-relaxed">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-3">
            {right.map((item, i) => (
              <div key={i + 3} className="border border-brand-border rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenIndex(openIndex === i + 3 ? null : i + 3)}
                  className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left"
                >
                  <span className="font-geist font-semibold text-sm md:text-base text-brand-text">{item.q}</span>
                  <i className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-brand-text/60 flex-shrink-0 transition-transform ${openIndex === i + 3 ? "rotate-180" : ""}`}></i>
                </button>
                {openIndex === i + 3 && (
                  <div className="px-5 pb-4">
                    <p className="font-geist text-sm text-brand-text/70 leading-relaxed">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
    </section>
  );
}

function SectionCTA() {
  return (
    <section className="bg-brand-yellow">
      <div className="max-w-[960px] mx-auto px-4 md:px-6 py-14 md:py-20 text-center">
        <h2 className="font-geist font-bold text-2xl md:text-3xl lg:text-4xl text-brand-text tracking-tight mb-4">
          Raam kapot of klemt ? Bel direct !
        </h2>
        <p className="font-geist text-base md:text-lg text-brand-text/70 mb-7 max-w-xl mx-auto">
          Bel ons direct voor een spoedklus of plan een gratis offerte-afspraak in. Wij zijn binnen 48 uur bij u op locatie in Eindhoven en omgeving.
        </p>
        <a
          href="tel:+31407987470"
          className="inline-flex items-center justify-center gap-2 bg-brand-text text-white font-geist font-semibold text-sm px-8 py-4 rounded-md hover:bg-brand-text/90 transition-colors whitespace-nowrap"
        >
          <i className="ri-phone-line w-5 h-5 flex items-center justify-center text-base"></i>
          Bel 040 798 7470
        </a>
      </div>
    </section>
  );
}

export default function RamenReparatiePage() {
  return (
    <main className="bg-[#FAFAFA] min-h-screen">
      <title>Ramen Reparatie Eindhoven | 48u Service, Vaste Prijs</title>
      <meta
        name="description"
        content="Raam kapot, klemt of sluit niet meer ? Wij repareren en vervangen ramen in Eindhoven. 48u-spoedservice, vaste prijzen vanaf €75, 10 jaar garantie."
      />
      <meta property="og:title" content="Ramen Reparatie Eindhoven | 48u Service, Vaste Prijs" />
      <meta property="og:description" content="Raam kapot, klemt of sluit niet meer ? Wij repareren en vervangen ramen in Eindhoven. 48u-spoedservice, vaste prijzen." />
      <meta property="og:locale" content="nl_NL" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://ramen-repair-eindhoven.nl/ramen-reparatie/" />
      <meta property="og:image" content="/og/og-home.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <link rel="canonical" href="https://ramen-repair-eindhoven.nl/ramen-reparatie/" />
      <link rel="alternate" hreflang="nl-NL" href="https://ramen-repair-eindhoven.nl/ramen-reparatie/" />
      <link rel="alternate" hreflang="x-default" href="https://ramen-repair-eindhoven.nl/ramen-reparatie/" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Ramen Reparatie Eindhoven | 48u Service, Vaste Prijs" />
      <meta name="twitter:description" content="Raam kapot, klemt of sluit niet meer ? Wij repareren en vervangen ramen in Eindhoven." />
      <SchemaMarkup />
      <TopBar />
      <Header />
      <Breadcrumb />
      <SectionHero />
      <SectionWanneer />
      <SectionDiensten />
      <SectionVervangenRepareren />
      <SectionPrijzen />
      <SectionWaaromWij />
      <SectionFAQ />
      <SectionCTA />
      <Footer />
      <StickyMobile />
    </main>
  );
}